// AGC: Base blockchain network configurations for wagmi
// AGC: Supports Hardhat Local, Base Sepolia (testnet) and Base Mainnet
// AGC: Reference: https://docs.base.org/learn/welcome
// AGC: Shared across all FarmProHub apps

import { Chain } from 'wagmi/chains'

/**
 * AGC: Hardhat Local Network for development
 * AGC: Chain ID: 31337
 * AGC: Local blockchain with test accounts
 */
export const hardhat: Chain = {
  id: 31337,
  name: 'Hardhat',
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['http://127.0.0.1:8545'],
    },
    public: {
      http: ['http://127.0.0.1:8545'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Hardhat',
      url: 'http://localhost:8545',
    },
  },
  testnet: true,
}

/**
 * AGC: Base Sepolia testnet configuration
 * AGC: Chain ID: 84532
 * AGC: Free testnet ETH available from Base faucet
 * AGC: Use for development and testing before mainnet deployment
 */
export const baseSepolia: Chain = {
  id: 84532,
  name: 'Base Sepolia',
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://sepolia.base.org'],
      webSocket: ['wss://sepolia.base.org'],
    },
    public: {
      http: ['https://sepolia.base.org'],
      webSocket: ['wss://sepolia.base.org'],
    },
  },
  blockExplorers: {
    default: {
      name: 'BaseScan',
      url: 'https://sepolia.basescan.org',
      apiUrl: 'https://api-sepolia.basescan.org/api',
    },
  },
  contracts: {
    multicall3: {
      address: '0xcA11bde05977b3631167028862bE2a173976CA11',
      blockCreated: 1059647,
    },
  },
  testnet: true,
}

/**
 * AGC: Base Mainnet configuration
 * AGC: Chain ID: 8453
 * AGC: Production-ready Ethereum L2 by Coinbase
 * AGC: Low fees, fast transactions, EVM-compatible
 */
export const base: Chain = {
  id: 8453,
  name: 'Base',
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://mainnet.base.org'],
      webSocket: ['wss://mainnet.base.org'],
    },
    public: {
      http: ['https://mainnet.base.org'],
      webSocket: ['wss://mainnet.base.org'],
    },
  },
  blockExplorers: {
    default: {
      name: 'BaseScan',
      url: 'https://basescan.org',
      apiUrl: 'https://api.basescan.org/api',
    },
  },
  contracts: {
    multicall3: {
      address: '0xcA11bde05977b3631167028862bE2a173976CA11',
      blockCreated: 5022,
    },
  },
  testnet: false,
}

/**
 * AGC: Get active network based on environment
 * AGC: Defaults to Hardhat for development
 */
export const getActiveNetwork = (): Chain => {
  const network = import.meta.env.VITE_BASE_NETWORK || 'hardhat'
  if (network === 'base') return base
  if (network === 'base-sepolia') return baseSepolia
  return hardhat
}

/**
 * AGC: All supported chains for wagmi configuration
 * AGC: Base Sepolia first since we're deployed there
 */
export const supportedChains: [Chain, ...Chain[]] = [baseSepolia, base, hardhat]

/**
 * AGC: Get USDC address for current network
 */
export const getUSDCAddress = (): string => {
  const network = import.meta.env.VITE_BASE_NETWORK || 'hardhat'
  if (network === 'base') {
    return import.meta.env.VITE_USDC_ADDRESS_MAINNET || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'
  }
  if (network === 'base-sepolia') {
    return import.meta.env.VITE_USDC_ADDRESS || '0x0000000000000000000000000000000000000000'
  }
  return '0xe7f1725E7734CE288F8367e1Bb143E90bb3F0512' // Hardhat local mock
}

